import {css, StyleSheet} from 'aphrodite';
import * as d3 from 'd3';
import {ScaleLinear} from 'd3';
import React, {useLayoutEffect, useRef} from 'react';

import {NucleotideType} from 'biojs/BioConstants';
import {ModifiedNucleotides} from 'biojs/Sequence';
import {SequenceType} from 'constants/SequenceConstants';
import {DEFAULT_PATTERN_HIGHLIGHT_FILL} from 'modbio/bulk-edit-mods/ModPatternConstants';
import {getUTFlexibleNaturalBasesIfEnabled} from 'modbio/util';
import {FontAvailability, getLetterSpacingCorrection} from 'molbio/maps/MonospaceFontCorrection';
import Type from 'molbio/maps/Type';
import * as sequtil from 'molbio/SeqUtil';
import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import {GRAY, NEAR_BLACK} from 'ui-library/Colors';

export const FONT_SIZE = 20;
export const LETTER_SPACING = 22;
export const AXIS_VMARGIN = 48;

function _emptyGroup(
  i: number,
  isNs: boolean = false
): {isNs: boolean; bases: Array<string>; startIdx: number} {
  return {isNs, bases: [], startIdx: i};
}

function splitOnNs(bases: string): Array<{isNs: boolean; bases: string; startIdx: number}> {
  const out = [];
  let thisGroup = _emptyGroup(0);
  Array.from(bases).forEach((b, i) => {
    if (b === 'N' || b === 'n') {
      if (!thisGroup.isNs) {
        out.push(thisGroup);
        thisGroup = _emptyGroup(i, true);
      }
    } else if (thisGroup.isNs) {
      out.push(thisGroup);
      thisGroup = _emptyGroup(i);
    }
    thisGroup.bases.push(b);
  });
  out.push(thisGroup);
  return out.map((group) => ({isNs: group.isNs, startIdx: group.startIdx, bases: group.bases.join('')}));
}

// cf. PolymerAxisView.getScale()
function getScale(
  xFn: ScaleLinear<number, number>,
  startIndex: number,
  polymerLength: number
): ScaleLinear<number, number> {
  const scale = xFn.copy();
  const domain = scale.domain().map((x) => sequtil.modelToDisplay(x, startIndex));
  scale.domain(domain);

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const originalTicks = scale.ticks;

  scale.ticks = function (m: number) {
    const ticks = originalTicks.apply(this, [m]);

    const domain = this.domain();
    if (ticks.length > 0 && ticks[ticks.length - 1] === domain[domain.length - 1]) {
      ticks.pop();
    }
    if (ticks.length === 0) {
      ticks.push(polymerLength);
    }
    return ticks;
  };

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const originalCopy = scale.copy;
  scale.copy = function () {
    // eslint-disable-next-line prefer-rest-params
    const copy = originalCopy.apply(this, arguments);

    // eslint-disable-next-line @typescript-eslint/unbound-method
    copy.ticks = this.ticks;
    return copy;
  };

  return scale;
}

export interface StaticBasesProps {
  bases: string;
  modifiedNucleotides: ModifiedNucleotides;
  nucleotideType: NucleotideType;
  sequenceType: SequenceType;
  fontSize: number;
  letterSpacing: number;
  vMargin: number;
  isTemplate: boolean;
}

export function StaticBases({
  bases,
  modifiedNucleotides,
  nucleotideType,
  sequenceType,
  fontSize,
  letterSpacing,
  vMargin,
  isTemplate,
}: StaticBasesProps): JSX.Element {
  if (FontAvailability.get().get('areFontsReady')) {
    letterSpacing += getLetterSpacingCorrection(fontSize);
  }

  const translateX = letterSpacing / 2;
  const translateY = new Type(fontSize, letterSpacing).height();
  const transform = `translate(${translateX}, ${translateY})`;

  const utAwareBases = getUTFlexibleNaturalBasesIfEnabled(
    bases,
    modifiedNucleotides,
    nucleotideType,
    sequenceType
  );

  return (
    <g className={css(styles.noPointerEvents)} transform={`translate(0,${vMargin})`}>
      <text fontSize={fontSize} letterSpacing={letterSpacing} transform={transform}>
        {isTemplate ? (
          splitOnNs(utAwareBases).map((group) => (
            <tspan key={group.startIdx} fill={group.isNs ? GRAY : NEAR_BLACK}>
              {group.bases}
            </tspan>
          ))
        ) : (
          <tspan>{utAwareBases}</tspan>
        )}
      </text>
    </g>
  );
}

// The height of the nucleotides axis
// This is determined by summing the tick length (8px), the line width (1px), and the tick font size (13 px by default)
export const AXIS_HEIGHT = 22;

const AXIS_TICK_SIZE = [8, 8, 0];
const AXIS_DEFAULT_COLOR = 'black';

export enum AxisOrientation {
  TOP = 'top',
  BOTTOM = 'bottom',
}

export function StaticAxis({
  basesLength,
  xFn,
  charWidth,
  vMargin,
  tickSpacing,
  axisOrientation,
  isReverse,
  color = AXIS_DEFAULT_COLOR,
}: {
  basesLength: number;
  charWidth: number;
  vMargin: number;
  xFn: ScaleLinear<number, number>;
  tickSpacing?: number;
  axisOrientation?: AxisOrientation;
  isReverse?: boolean;
  color?: string;
}): JSX.Element {
  const typeOffset = isReverse ? -(charWidth / 2) : charWidth / 2;
  const rootEl = useRef<SVGGElement | null>(null);

  useLayoutEffect(() => {
    // cf. PolymerAxisView.update()
    const scale = getScale(xFn, 1, basesLength);
    const axis = axisOrientation === AxisOrientation.TOP ? d3.axisTop(scale) : d3.axisBottom(scale);
    axis.tickSize(AXIS_TICK_SIZE[0]);
    axis.tickSizeInner(AXIS_TICK_SIZE[1]);
    axis.tickSizeOuter(AXIS_TICK_SIZE[2]);
    axis.ticks(Math.ceil(basesLength / (tickSpacing ?? 5)));

    if (rootEl.current != null) {
      const root = d3.select(rootEl.current);
      root.call(axis);
      root.select('path').attr('transform', `translate(${-typeOffset},0)`);
      root.selectAll('path, line').attr('stroke', color);
      root.selectAll('path, line').attr('stroke-width', 1);
    }
  }, [basesLength, xFn, typeOffset, tickSpacing, axisOrientation, color]);

  return (
    <g
      className={css(styles.noPointerEvents)}
      ref={rootEl}
      transform={`translate(${typeOffset},${vMargin})`}
    />
  );
}

export function getBaseCountForWidth(width: number): number {
  const type = new Type(FONT_SIZE, LETTER_SPACING);
  return type.numChars(width);
}

export function getWidthForBaseCount(baseCount: number): number {
  const type = new Type(FONT_SIZE, LETTER_SPACING);
  return type.width(baseCount) + type.letterSpacing;
}

const styles = StyleSheet.create({
  wrapper: {
    fontFamily: FONT_FAMILY_MONOSPACED,
    shapeRendering: 'crispEdges',
    width: '100%',
    height: 70,
  },
  noPointerEvents: {
    pointerEvents: 'none',
  },
  highlight: {
    fill: DEFAULT_PATTERN_HIGHLIGHT_FILL,
  },
});
